import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../services/garageApi';
import { useNavigate } from 'react-router-dom';
import {
  setAuthToken,
  getAuthToken,
  removeAuthToken,
  isTokenExpired,
  getTokenTimeRemaining,
  secureStorage
} from '../utils/securityUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenExpiryWarning, setTokenExpiryWarning] = useState(false);
  const navigate = useNavigate();
  const refreshTimerRef = useRef(null);
  const expiryCheckIntervalRef = useRef(null);
  const startTokenExpiryMonitoringRef = useRef(null);

  useEffect(() => {
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Define clearAuthData first since it's used by other functions
  const clearAuthData = useCallback(() => {
    // Clear secure storage
    removeAuthToken();
    secureStorage.removeItem('user');
    secureStorage.removeItem('tenant');
    secureStorage.removeItem('tenant_id');
    secureStorage.removeItem('activeTenantId');
    secureStorage.removeItem('is_super_admin');

    // Clear legacy localStorage (for migration)
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('activeTenantId');
    localStorage.removeItem('is_super_admin');

    // Clear state
    setUser(null);
    setTenant(null);
    setTokenExpiryWarning(false);

    // Clear timers
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (expiryCheckIntervalRef.current) {
      clearInterval(expiryCheckIntervalRef.current);
      expiryCheckIntervalRef.current = null;
    }
  }, []);

  const validateTokenInBackground = useCallback(async () => {
    try {
      console.log('🔄 AuthContext: Validating token in background...');
      const response = await authAPI.validate();

      let userData, tenantData;

      // Handle different response formats
      if (response.data?.success !== undefined) {
        if (response.data.success) {
          userData = response.data.data?.user || response.data.user;
          tenantData = response.data.data?.tenant || response.data.tenant;
        } else {
          throw new Error('Token validation failed');
        }
      } else {
        userData = response.data;
        tenantData = userData?.tenant;
      }

      if (userData?.id) {
        console.log('✅ AuthContext: Token validated, updating user data');
        setUser(userData);
        secureStorage.setItem('user', userData);

        if (tenantData?.id) {
          setTenant(tenantData);
          secureStorage.setItem('tenant', tenantData);
          secureStorage.setItem('tenant_id', tenantData.id);
        } else if (userData.tenant_id) {
          secureStorage.setItem('tenant_id', userData.tenant_id);
        }

        if (userData.super_admin) {
          secureStorage.setItem('is_super_admin', 'true');
        }
      }
    } catch (error) {
      console.error('⚠️ AuthContext: Token validation failed (background)', error);

      // Only logout if it's a 401 error (unauthorized)
      if (error.response?.status === 401) {
        console.log('❌ AuthContext: 401 error - clearing auth data');
        clearAuthData();
        // Only navigate to login if we're not already there
        if (window.location.pathname !== '/login') {
          navigate('/login');
        }
      } else {
        console.log('⚠️ AuthContext: Non-401 error, keeping current session');
        // For other errors (network, server), keep the user logged in
      }
    }
  }, [clearAuthData, navigate]);

  // Refresh token - defined before startTokenExpiryMonitoring to avoid reference issues
  const refreshToken = useCallback(async () => {
    try {
      console.log('🔄 Refreshing token...');
      const response = await authAPI.refresh();

      if (response.data?.token) {
        setAuthToken(response.data.token, 60 * 60 * 1000);
        setTokenExpiryWarning(false);
        // Restart monitoring after refresh using ref to avoid circular dependency
        if (startTokenExpiryMonitoringRef.current) {
          startTokenExpiryMonitoringRef.current();
        }
        console.log('✅ Token refreshed successfully');
      }
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      // If refresh fails, show warning to user
      setTokenExpiryWarning(true);
    }
  }, []);

  // Token expiry monitoring
  const startTokenExpiryMonitoring = useCallback(() => {
    // Clear existing intervals
    if (expiryCheckIntervalRef.current) {
      clearInterval(expiryCheckIntervalRef.current);
    }
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Check token expiry every minute
    expiryCheckIntervalRef.current = setInterval(() => {
      const timeRemaining = getTokenTimeRemaining();

      if (timeRemaining === 0) {
        console.log('⏰ Token expired, logging out');
        clearAuthData();
        navigate('/login');
        return;
      }

      // Show warning 5 minutes before expiry
      if (timeRemaining < 5 * 60 * 1000 && timeRemaining > 0) {
        setTokenExpiryWarning(true);
      } else {
        setTokenExpiryWarning(false);
      }
    }, 60 * 1000); // Check every minute

    // Schedule auto-refresh at 50 minutes (10 minutes before 60-minute expiry)
    const timeRemaining = getTokenTimeRemaining();
    const refreshTime = Math.max(0, timeRemaining - (10 * 60 * 1000));

    if (refreshTime > 0) {
      refreshTimerRef.current = setTimeout(() => {
        refreshToken();
      }, refreshTime);
    }
  }, [navigate, clearAuthData, refreshToken]);

  // Store the function in a ref so refreshToken can call it
  startTokenExpiryMonitoringRef.current = startTokenExpiryMonitoring;

  const initializeAuth = async () => {
    const token = getAuthToken();
    const savedUser = secureStorage.getItem('user');
    const savedTenant = secureStorage.getItem('tenant');

    console.log('🔐 AuthContext: Initializing...', {
      hasToken: !!token,
      hasUser: !!savedUser,
      hasTenant: !!savedTenant,
      isExpired: isTokenExpired()
    });

    if (!token || isTokenExpired()) {
      console.log('❌ AuthContext: No valid token found');
      clearAuthData();
      setLoading(false);
      return;
    }

    // Use saved user data first to prevent logout
    if (savedUser) {
      try {
        console.log('✅ AuthContext: Using saved user data');
        setUser(savedUser);

        if (savedTenant) {
          setTenant(savedTenant);
        }
      } catch (e) {
        console.error('❌ AuthContext: Error loading saved data', e);
      }
    }

    // Start token expiry monitoring
    startTokenExpiryMonitoring();

    // Then validate token in background (don't await)
    validateTokenInBackground();

    // Set loading to false immediately after restoring from storage
    setLoading(false);
  };

  const login = async (credentials) => {
    try {
      console.log('🔐 AuthContext: Starting login...');
      const response = await authAPI.login(credentials);

      let responseData = response.data;

      if (responseData.data && responseData.success !== undefined) {
        responseData = responseData.data;
      }

      const { token, user: userData, tenant: userTenant } = responseData;

      if (!token || !userData) {
        throw new Error('Invalid response: missing token or user data');
      }

      // Store token with 60-minute expiration (matching Laravel backend)
      setAuthToken(token, 60 * 60 * 1000);
      secureStorage.setItem('user', userData);

      if (userData.super_admin) {
        secureStorage.setItem('is_super_admin', 'true');
      }

      if (userTenant?.id) {
        secureStorage.setItem('tenant', userTenant);
        secureStorage.setItem('tenant_id', userTenant.id);
        setTenant(userTenant);
      } else if (userData.tenant_id) {
        secureStorage.setItem('tenant_id', userData.tenant_id);
      }

      setUser(userData);

      // Start monitoring token expiry
      startTokenExpiryMonitoring();

      console.log('✅ AuthContext: Login successful');

      return response;
    } catch (error) {
      console.error('❌ AuthContext: Login error', error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      let responseData = response.data;

      if (responseData.data && responseData.success !== undefined) {
        responseData = responseData.data;
      }

      const { token, user, tenant: userTenant } = responseData;

      if (!token || !user) {
        throw new Error('Invalid response: missing token or user data');
      }

      // Store token with 60-minute expiration
      setAuthToken(token, 60 * 60 * 1000);
      secureStorage.setItem('user', user);

      if (user.super_admin) {
        secureStorage.setItem('is_super_admin', 'true');
      }

      if (userTenant?.id) {
        secureStorage.setItem('tenant', userTenant);
        secureStorage.setItem('tenant_id', userTenant.id);
        setTenant(userTenant);
      } else if (user.tenant_id) {
        secureStorage.setItem('tenant_id', user.tenant_id);
      }

      setUser(user);

      // Start monitoring token expiry
      startTokenExpiryMonitoring();

      return response;
    } catch (error) {
      console.error('❌ AuthContext: Registration error', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('❌ Logout API error:', error);
    }

    clearAuthData();
    navigate('/login');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (expiryCheckIntervalRef.current) {
        clearInterval(expiryCheckIntervalRef.current);
      }
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const value = {
    user,
    tenant,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
    isTenantAdmin: user?.tenant_manager || false,
    isSuperAdmin: user?.super_admin || false,
    tokenExpiryWarning,
    tokenTimeRemaining: getTokenTimeRemaining,
    refreshToken,
    canAccessTenant: (tenantId) => {
      if (user?.super_admin) return true;
      return user?.tenant_id === tenantId;
    },
    canAccessAllTenants: user?.super_admin || false,
    refreshAuth: initializeAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};